import { useState, useCallback, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  FileText, 
  CheckCircle, 
  AlertCircle,
  Loader2,
  Trash2,
  Save,
  FileUp,
  Fuel
} from 'lucide-react';
import { useExpenses } from '../contexts/ExpenseContext';
import BulkUploadSection from '../components/BulkUploadSection';
import * as pdfjsLib from 'pdfjs-dist';
// @ts-ignore - Vite URL import for worker
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

// Set up PDF.js worker - use bundled worker via Vite
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

interface ParsedFuelExpense {
  id: string;
  date: string;
  location: string;
  fuelAmount: number;
  fuelDiscount: number;
  totalAmount: number;
  selected: boolean;
  // Enhanced fields for smart duplicate detection
  storeNumber?: string;
  cardLast4?: string;
  transactionId?: string; // Invoice/auth number
}

export default function ImportComdata() {
  const { addExpense, expenses } = useExpenses();
  const [pdfText, setPdfText] = useState('');
  const [parsedFuel, setParsedFuel] = useState<ParsedFuelExpense[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [importStatus, setImportStatus] = useState<{ fuel: number; errors: number; skipped: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Target tax year for import
  const currentYear = new Date().getFullYear();

  // Normalize fragmented PDF text - fixes common extraction issues
  const normalizeText = (text: string): string => {
    let result = text;
    
    // Fix common word fragments (PDF often splits words with spaces)
    const wordFixes: [RegExp, string][] = [
      [/T\s*R\s*K\s*D\s*S/gi, 'TRKDS'],
      [/TRKD\s+S/gi, 'TRKDS'],
      [/D\s*F\s*C\s*Q/gi, 'DFCQ'],
      [/S\s*T\s*O\s*R\s*E/gi, 'STORE'],
      [/L\s*o\s*v\s*e\s*['']\s*s/gi, "Love's"],
      [/•aves/gi, "Love's"],
      [/Ga\s*l\s*l\s*o\s*n\s*s/gi, 'Gallons'],
      [/G\s*a\s*l\s*l\s*o\s*n\s*s/gi, 'Gallons'],
      [/O\s*d\s*o\s*m\s*e\s*t\s*e\s*r/gi, 'Odometer'],
      [/Odo\s*m\s*et\s*er/gi, 'Odometer'],
      [/T\s*o\s*t\s*a\s*l/gi, 'Total'],
      [/To\s*t\s*al/gi, 'Total'],
      [/S\s*a\s*l\s*e/gi, 'Sale'],
      [/S\s*u\s*b\s*t\s*o\s*t/gi, 'Subtot'],
      [/P\s*u\s*m\s*p/gi, 'Pump'],
      [/P\s*r\s*i\s*c\s*e/gi, 'Price'],
      [/I\s*N\s*V\s*O\s*I\s*C\s*E/gi, 'INVOICE'],
      [/Hu\s*l\s*l/gi, 'Hull'],
      [/Bi\s*lo\s*xi/gi, 'Biloxi'],
      [/Sa\s*li\s*na/gi, 'Salina'],
      [/Spa\s*r\s*ta/gi, 'Sparta'],
      [/Falk\s*ville/gi, 'Falkville'],
      [/Wei\s*mar/gi, 'Weimar'],
      [/Tol\s*le\s*son/gi, 'Tolleson'],
    ];
    
    for (const [pattern, replacement] of wordFixes) {
      result = result.replace(pattern, replacement);
    }
    
    // Fix number fragments: "31 . 64" -> "31.64", "57 61" -> "57.61"
    result = result
      .replace(/(\d)\s*\.\s*(\d)/g, '$1.$2')
      .replace(/(\d)\s+(\d{2})(?=\s|$)/g, '$1.$2')
      .replace(/\$\s+/g, '$');
    
    // Fix state code fragments: ", A L" -> ", AL"
    result = result
      .replace(/,\s*([A-Z])\s+([A-Z])(?=\s|\()/g, ', $1$2')
      .replace(/([A-Z])\s+([A-Z])(?=\s*\(|\s*\d{5})/g, '$1$2');
    
    // Normalize whitespace
    result = result.replace(/\s{2,}/g, ' ');
    
    return result;
  };
  const [targetYear, setTargetYear] = useState<number | 'auto'>('auto');
  const availableYears = Array.from({ length: currentYear - 2019 }, (_, i) => currentYear - i);

  // Smart duplicate check - considers date range, store number, amount, and transaction ID
  const isDuplicate = useCallback((fuel: ParsedFuelExpense): boolean => {
    const { date, fuelAmount, storeNumber, transactionId } = fuel;
    
    return expenses.some(exp => {
      if (exp.category !== 'fuel') return false;
      
      // If we have a transaction ID and it matches, definitely duplicate
      if (transactionId && exp.transactionId === transactionId) return true;
      
      // Check amount match (within $0.02)
      const amountMatch = exp.amount && Math.abs(exp.amount - fuelAmount) < 0.02;
      if (!amountMatch) return false;
      
      // Check date match (exact or within 1 day for bank posting delays)
      const expDate = new Date(exp.date);
      const fuelDate = new Date(date);
      const daysDiff = Math.abs(expDate.getTime() - fuelDate.getTime()) / (1000 * 60 * 60 * 24);
      if (daysDiff > 1) return false;
      
      // Same date + same amount = likely duplicate
      // If store numbers match too, definitely duplicate
      if (storeNumber && exp.storeNumber === storeNumber) return true;
      
      // Same exact date and amount without store number = still flag as duplicate
      if (exp.date === date) return true;
      
      // Different day but within 1 day - only flag if description/location matches
      if (exp.description && fuel.location) {
        const descNorm = exp.description.toLowerCase().replace(/[^a-z0-9]/g, '');
        const locNorm = fuel.location.toLowerCase().replace(/[^a-z0-9]/g, '');
        if (descNorm.includes(locNorm) || locNorm.includes(descNorm)) return true;
      }
      
      return false;
    });
  }, [expenses]);

  // Fix OCR errors in dates (common: 8 misread as 0, especially at start)
  const fixOcrDate = (dateStr: string): string => {
    // Fix dates that start with 8 instead of 0 (e.g., 89/19/25 -> 09/19/25)
    return dateStr.replace(/^8(\d)/, '0$1');
  };

  // Parse date from various formats and return YYYY-MM-DD
  const parseDate = (section: string): string | null => {
    // Try MM/DD/YY or MM/DD/YYYY format
    let dateMatch = section.match(/(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})/);
    if (dateMatch) {
      let [, month, day, year] = dateMatch;
      // Fix OCR errors
      month = fixOcrDate(month);
      day = fixOcrDate(day);
      const fullYear = year.length === 2 ? `20${year}` : year;
      return `${fullYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    
    // Try MM-DD-YYYY format (like Indiana Toll: 09-19-2025)
    dateMatch = section.match(/(\d{1,2})-(\d{1,2})-(\d{4})/);
    if (dateMatch) {
      let [, month, day, year] = dateMatch;
      month = fixOcrDate(month);
      day = fixOcrDate(day);
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    
    return null;
  };

  // Parse Love's OCR receipts - clean format from Adobe OCR
  // Split by receipt START marker (~ followed by Love's branding)
  const parseLovesOCR = (text: string): ParsedFuelExpense[] => {
    const expenses: ParsedFuelExpense[] = [];
    console.log('=== LOVES OCR PARSER ===');
    
    // Split by receipt START - each receipt starts with ~ •aves, ~ •oves, or ~ love's
    const receiptBlocks = text.split(/(?=~\s*[•l])/i);
    console.log('Found', receiptBlocks.length, 'receipt blocks');
    
    for (const block of receiptBlocks) {
      if (block.trim().length < 100) continue;
      
      // Check if this is a FUEL receipt 
      // TRKDS = Truck Diesel (fleet card code) - this is the PRIMARY indicator for binder receipts
      // Must have (DIESEL, DEF, or TRKDS) with Pump:, or bottled products
      const hasTrkdsPump = /T\s*R\s*K\s*D\s*S/i.test(block) && /Pump\s*:/i.test(block); // Fleet card diesel
      const hasDieselPump = /D\s*I\s*E\s*S\s*E\s*L/i.test(block) && /Pump\s*:/i.test(block);
      const hasDefPump = /\bDEF\b/i.test(block) && /Pump\s*:/i.test(block);
      const hasLovesDef = /LOVES\s*DEF/i.test(block); // Bottled DEF product
      const hasPeakDiesel = /Peak\s*Blue\s*Dsl/i.test(block); // Bottled diesel additive
      
      const isFuelReceipt = hasTrkdsPump || hasDieselPump || hasDefPump || hasLovesDef || hasPeakDiesel;
      
      if (!isFuelReceipt) {
        continue;
      }
      
      // Extract store number
      const storeMatch = block.match(/STORE\s*(\d{2,4})/i);
      const storeNum = storeMatch ? storeMatch[1] : '';
      
      // Extract city and state - look for pattern after street address
      let city = '', state = '';
      const locationMatch = block.match(/\n([A-Za-z][A-Za-z\s]{1,20}),?\s*([A-Z]{2})\s*\n/);
      if (locationMatch) {
        city = locationMatch[1].trim().replace(/\s+/g, ' ');
        state = locationMatch[2];
      }
      
      // Extract date - MM/DD/YYYY format
      const dateMatch = block.match(/Dat\s*e:\s*(\d{2})\/(\d{2})\/(\d{4})/i) ||
                       block.match(/(\d{2})\/(\d{2})\/(\d{4})/);
      if (!dateMatch) {
        console.log('Skipping block - no date found');
        continue;
      }
      const [, month, day, year] = dateMatch;
      const formattedDate = `${year}-${month}-${day}`;
      
      // Extract the Total Sale amount - this is the most reliable indicator
      // Look for "Total Sale:" pattern first, then fall back to last $XX.XX
      let amount = 0;
      
      // PRIMARY: Look for "Total Sale: $XX.XX" or "Total Sale: $XX .XX" (OCR may add spaces)
      const totalSaleMatch = block.match(/Total\s+Sale[\s:]*\$?\s*(-?\d+)\s*\.?\s*(\d{2})/i);
      if (totalSaleMatch) {
        const dollars = totalSaleMatch[1];
        const cents = totalSaleMatch[2];
        amount = parseFloat(`${dollars}.${cents}`);
        console.log(`  Found Total Sale: $${amount}`);
      }
      
      // FALLBACK: Use the LAST $XX.XX pattern in the block
      if (amount === 0) {
        const dollarMatches = [...block.matchAll(/\$\s*(-?\d+)\s*\.?\s*(\d{2})/g)];
        if (dollarMatches.length > 0) {
          const lastDollar = dollarMatches[dollarMatches.length - 1];
          const dollars = lastDollar[1];
          const cents = lastDollar[2];
          amount = parseFloat(`${dollars}.${cents}`);
          console.log(`  Found last $: $${amount}`);
        }
      }
      
      // Skip invalid amounts
      if (isNaN(amount) || amount <= 0 || amount < 10 || amount > 500) {
        console.log(`Skipping block - invalid amount: $${amount} for store ${storeNum}`);
        continue;
      }
      
      // Extract gallons - handle "Gal lons:", "Gallons:", etc.
      const gallonsMatch = block.match(/Gal\s*l?\s*ons\s*:?\s*([\d.]+)/i);
      const gallons = gallonsMatch ? parseFloat(gallonsMatch[1]) : 0;
      
      // Build location string
      let location = "Love's";
      if (storeNum) location += ` #${storeNum}`;
      if (city && state) location += ` - ${city}, ${state}`;
      if (gallons > 0) location += ` (${gallons.toFixed(1)} gal)`;
      
      // Check for duplicates (same date + store + similar amount within $1)
      const exists = expenses.some(e => 
        e.date === formattedDate && 
        e.location.includes(`#${storeNum}`) &&
        Math.abs(e.totalAmount - amount) < 1.00
      );
      
      if (!exists) {
        expenses.push({
          id: crypto.randomUUID(),
          date: formattedDate,
          location,
          fuelAmount: amount,
          fuelDiscount: 0,
          totalAmount: amount,
          selected: true
        });
        console.log(`✅ OCR Fuel: ${formattedDate} | Store #${storeNum} | ${city}, ${state} | $${amount} | ${gallons} gal`);
      } else {
        console.log(`⏭️ Duplicate skipped: ${formattedDate} | Store #${storeNum} | $${amount}`);
      }
    }
    
    console.log('=== OCR Parser found:', expenses.length, 'fuel receipts ===');
    return expenses;
  };

  // Parse Love's receipts (regular format)
  const parseLoves = (section: string): ParsedFuelExpense | null => {
    if (!section.match(/Love's|LOVE'S|Welcome To Loves/i)) return null;
    
    const date = parseDate(section);
    if (!date) return null;
    
    // Extract store number and location
    const storeMatch = section.match(/(?:STORE|Loves?\s*#?)\s*(\d+)/i);
    const cityMatch = section.match(/([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*),?\s+([A-Z]{2})\s+\d{5}/i) ||
                     section.match(/([A-Z][a-z]+)\s+([A-Z]{2})\s*$/m);
    
    let location = "Love's";
    if (storeMatch) location += ` #${storeMatch[1]}`;
    if (cityMatch) location += ` - ${cityMatch[1]}, ${cityMatch[2]}`;
    
    // Extract gallons
    const gallonsMatch = section.match(/Gallons[:\s]*(\d+\.?\d*)/i) ||
                        section.match(/(\d+\.?\d*)\s*(?:GAL|G)\s*@/i);
    if (!gallonsMatch) return null;
    const gallons = parseFloat(gallonsMatch[1]);
    if (gallons < 1 || gallons > 200) return null;
    
    // Extract total
    const totalMatch = section.match(/(?:Total Sale|TOTAL SALE)[:\s]*\$?\s*([\d.]+)/i) ||
                      section.match(/Total[:\s]*\$?\s*([\d.]+)/i);
    if (!totalMatch) return null;
    const total = parseFloat(totalMatch[1]);
    if (total < 3 || total > 500) return null;
    
    // Extract savings
    const savingsMatch = section.match(/(?:You saved|Savings)[:\s]*\$?\s*([\d.]+)/i);
    const savings = savingsMatch ? parseFloat(savingsMatch[1]) : 0;
    
    return {
      id: crypto.randomUUID(),
      date,
      location,
      fuelAmount: total,
      fuelDiscount: savings,
      totalAmount: total,
      selected: true
    };
  };

  // Parse Love's Fleet Card receipts (TRKDS format from binder PDFs)
  // These have TRKDS = Truck Diesel, paid via DFCQ fleet card
  // Expects pre-normalized text
  // 
  // IMPROVED: Now uses "Total Sale:" as PRIMARY method since TRKDS and amount
  // are often on separate lines in OCR'd PDFs (column separation issue)
  const parseLovesFleetCard = (normalized: string): ParsedFuelExpense[] => {
    const expenses: ParsedFuelExpense[] = [];
    console.log('=== LOVES FLEET CARD PARSER (v2) ===');
    console.log('Text length:', normalized.length);
    
    // Find all dates - MM/DD/YYYY format
    const datePattern = /(\d{2})\/(\d{2})\/(\d{4})/g;
    const dates = [...normalized.matchAll(datePattern)];
    console.log('Dates found:', dates.length);
    
    // Find all store numbers
    const storePattern = /STORE\s*(\d{3,4})/gi;
    const stores = [...normalized.matchAll(storePattern)];
    console.log('Stores found:', stores.length);
    
    // Find all locations (City, ST) - handles various formats
    const locationPattern = /([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*),\s*([A-Z]{2})(?:\s|\(|$)/gm;
    const locations = [...normalized.matchAll(locationPattern)];
    console.log('Locations found:', locations.length);
    
    // Find all gallons - various formats
    const gallonsPattern = /Gal\s*l?\s*ons[\s|:]*([\d]+\.[\d]+)/gi;
    const gallonsAll = [...normalized.matchAll(gallonsPattern)];
    console.log('Gallons found:', gallonsAll.length);
    
    // PRIMARY METHOD: Find "Total Sale:" amounts - most reliable for Love's binder PDFs
    // Handles: "Total Sale: $86.13", "Total Sale $86.13", "Total Sale: 86 .13", etc.
    // Also handles OCR variations with spaces: "Total Sale: $86 .13"
    const totalSalePattern = /Total\s+Sale[\s:]*\$?\s*([\d]+)\s*\.?\s*([\d]{2})/gi;
    const totalSaleMatches = [...normalized.matchAll(totalSalePattern)];
    console.log('Total Sale matches:', totalSaleMatches.length, 
      totalSaleMatches.map(m => `$${m[1]}.${m[2]}`));
    
    // Also find TRKDS amounts for combined TRKDS+DEF receipts
    const trkdsPattern = /TRKDS[\s|]*([\d]+\.[\d]{2})/gi;
    const trkdsMatches = [...normalized.matchAll(trkdsPattern)];
    console.log('TRKDS matches:', trkdsMatches.length, trkdsMatches.map(m => m[1]));
    
    // Find DEF amounts (for reference, usually combined with TRKDS)
    const defPattern = /DEF[\s|]*([\d]+\.[\d]{2})/gi;
    const defMatches = [...normalized.matchAll(defPattern)];
    console.log('DEF matches:', defMatches.length);
    
    // Helper: Check if this is a fuel receipt (has TRKDS, DEF, or DIESEL nearby)
    const isFuelContext = (pos: number, range: number = 2000): boolean => {
      const before = normalized.substring(Math.max(0, pos - range), pos);
      const after = normalized.substring(pos, Math.min(normalized.length, pos + range));
      const context = before + after;
      return /TRKDS|DEF|DIESEL|Pump:/i.test(context);
    };
    
    // Helper: Check if this is a cash advance or shower (skip these)
    const isNonFuel = (pos: number, range: number = 1000): boolean => {
      const before = normalized.substring(Math.max(0, pos - range), pos);
      const after = normalized.substring(pos, Math.min(normalized.length, pos + range));
      const context = before + after;
      return /CASH\s*ADVANCE|SHOWER|PAY\s*OUT|PAY\s*IN|FEE\s*ITEM|REFUND|VOID/i.test(context);
    };
    
    // Process "Total Sale:" amounts first (most reliable)
    console.log('--- Processing Total Sale amounts ---');
    for (const match of totalSaleMatches) {
      const wholePart = match[1];
      const decimalPart = match[2];
      const amount = parseFloat(`${wholePart}.${decimalPart}`);
      const matchPos = match.index || 0;
      
      // Skip non-fuel amounts
      if (amount < 15 || amount > 200) {
        console.log(`⏭️ Skip out of range: $${amount}`);
        continue;
      }
      
      // Skip non-fuel transactions (cash advances, showers, etc.)
      if (isNonFuel(matchPos)) {
        console.log(`⏭️ Skip non-fuel context: $${amount}`);
        continue;
      }
      
      // Verify this is a fuel receipt (has TRKDS, DEF, or DIESEL nearby)
      if (!isFuelContext(matchPos)) {
        console.log(`⏭️ Skip no fuel context: $${amount}`);
        continue;
      }
      
      // Find nearest date (look backwards and forwards)
      let nearestDate = null;
      let minDist = Infinity;
      for (const d of dates) {
        const dist = Math.abs((d.index || 0) - matchPos);
        // Prefer dates BEFORE the total (receipt flows top to bottom)
        const adjustedDist = (d.index || 0) < matchPos ? dist : dist + 500;
        if (adjustedDist < minDist) {
          minDist = adjustedDist;
          nearestDate = d;
        }
      }
      
      if (!nearestDate) {
        console.log(`⏭️ Skip no date found: $${amount}`);
        continue;
      }
      
      const [, month, day, year] = nearestDate;
      const formattedDate = `${year}-${month}-${day}`;
      
      // Find nearest store
      let storeNum = '';
      minDist = Infinity;
      for (const s of stores) {
        const dist = Math.abs((s.index || 0) - matchPos);
        if (dist < minDist && dist < 3000) {
          minDist = dist;
          storeNum = s[1];
        }
      }
      
      // Find nearest location
      let city = '', state = '';
      minDist = Infinity;
      for (const loc of locations) {
        const dist = Math.abs((loc.index || 0) - matchPos);
        if (dist < minDist && dist < 3000) {
          minDist = dist;
          city = loc[1];
          state = loc[2];
        }
      }
      
      // Find nearest gallons
      let gallons = 0;
      minDist = Infinity;
      for (const g of gallonsAll) {
        const dist = Math.abs((g.index || 0) - matchPos);
        if (dist < minDist && dist < 1500) {
          minDist = dist;
          gallons = parseFloat(g[1]);
        }
      }
      
      // Check for duplicates (same date and similar amount)
      const exists = expenses.some(e => 
        e.date === formattedDate && Math.abs(e.totalAmount - amount) < 0.50
      );
      
      if (!exists) {
        let location = "Love's";
        if (storeNum) location += ` #${storeNum}`;
        if (city && state) location += ` - ${city}, ${state}`;
        if (gallons > 0) location += ` (${gallons.toFixed(1)} gal)`;
        
        expenses.push({
          id: crypto.randomUUID(),
          date: formattedDate,
          location,
          fuelAmount: amount,
          fuelDiscount: 0,
          totalAmount: amount,
          selected: true
        });
        console.log(`✅ Added: ${formattedDate} | ${location} | $${amount}`);
      } else {
        console.log(`⏭️ Duplicate: ${formattedDate} | $${amount}`);
      }
    }
    
    // SECONDARY: Process any TRKDS matches that weren't captured by Total Sale
    console.log('--- Processing TRKDS amounts (secondary) ---');
    for (const match of trkdsMatches) {
      const amount = parseFloat(match[1]);
      const matchPos = match.index || 0;
      
      if (amount < 15 || amount > 200) continue;
      
      // Find nearest date
      let nearestDate = null;
      let minDist = Infinity;
      for (const d of dates) {
        const dist = Math.abs((d.index || 0) - matchPos);
        if (dist < minDist) {
          minDist = dist;
          nearestDate = d;
        }
      }
      
      if (!nearestDate) continue;
      
      const [, month, day, year] = nearestDate;
      const formattedDate = `${year}-${month}-${day}`;
      
      // Check for duplicates (may have been added by Total Sale)
      const exists = expenses.some(e => 
        e.date === formattedDate && Math.abs(e.totalAmount - amount) < 5.00
      );
      
      if (exists) {
        console.log(`⏭️ TRKDS already captured: ${formattedDate} | $${amount}`);
        continue;
      }
      
      // Find nearest store
      let storeNum = '';
      minDist = Infinity;
      for (const s of stores) {
        const dist = Math.abs((s.index || 0) - matchPos);
        if (dist < minDist && dist < 1500) {
          minDist = dist;
          storeNum = s[1];
        }
      }
      
      // Find nearest location
      let city = '', state = '';
      minDist = Infinity;
      for (const loc of locations) {
        const dist = Math.abs((loc.index || 0) - matchPos);
        if (dist < minDist && dist < 1500) {
          minDist = dist;
          city = loc[1];
          state = loc[2];
        }
      }
      
      // Find nearest gallons
      let gallons = 0;
      minDist = Infinity;
      for (const g of gallonsAll) {
        const dist = Math.abs((g.index || 0) - matchPos);
        if (dist < minDist && dist < 1000) {
          minDist = dist;
          gallons = parseFloat(g[1]);
        }
      }
      
      let location = "Love's";
      if (storeNum) location += ` #${storeNum}`;
      if (city && state) location += ` - ${city}, ${state}`;
      if (gallons > 0) location += ` (${gallons.toFixed(1)} gal)`;
      
      expenses.push({
        id: crypto.randomUUID(),
        date: formattedDate,
        location,
        fuelAmount: amount,
        fuelDiscount: 0,
        totalAmount: amount,
        selected: true
      });
      console.log(`✅ Added TRKDS: ${formattedDate} | ${location} | $${amount}`);
    }
    
    // Sort by date
    expenses.sort((a, b) => a.date.localeCompare(b.date));
    
    console.log('=== Fleet card parser found:', expenses.length, 'fuel entries ===');
    return expenses;
  };

  // Parse Pilot/Flying J receipts
  const parsePilotFlyingJ = (section: string): ParsedFuelExpense | null => {
    const isPilot = section.match(/Pilot\s*#?\s*(\d+)/i);
    const isFlyingJ = section.match(/Flying J\s*#?\s*(\d+)/i);
    if (!isPilot && !isFlyingJ) return null;
    
    const date = parseDate(section);
    if (!date) return null;
    
    // Extract store info
    const storeMatch = isPilot || isFlyingJ;
    const brand = isPilot ? 'Pilot' : 'Flying J';
    const storeNum = storeMatch ? storeMatch[1] : '';
    
    // Extract city/state from address
    const cityMatch = section.match(/([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*),?\s+([A-Z]{2})\s+\d{5}/i) ||
                     section.match(/([A-Z][a-z]+)\s+([A-Z]{2})\s*$/m);
    
    let location = `${brand}${storeNum ? ' #' + storeNum : ''}`;
    if (cityMatch) location += ` - ${cityMatch[1]}, ${cityMatch[2]}`;
    
    // Extract gallons - "Pump Gallons Price" format or "X.XXX GAL"
    const gallonsMatch = section.match(/(?:Pump\s+)?(\d+\.?\d*)\s*(?:GAL|G)?\s*\$?\s*\d+\.\d{2,3}/i) ||
                        section.match(/(\d+\.\d+)\s+\$\s*\d+\.\d{3}/);
    if (!gallonsMatch) return null;
    const gallons = parseFloat(gallonsMatch[1]);
    if (gallons < 1 || gallons > 200) return null;
    
    // Extract total
    const totalMatch = section.match(/Total Sale[:\s]*\$?\s*([\d.]+)/i);
    if (!totalMatch) return null;
    const total = parseFloat(totalMatch[1]);
    if (total < 3 || total > 500) return null;
    
    // Extract savings
    const savingsMatch = section.match(/You saved[:\s]*\$?\s*([\d.]+)/i);
    const savings = savingsMatch ? parseFloat(savingsMatch[1]) : 0;
    
    return {
      id: crypto.randomUUID(),
      date,
      location,
      fuelAmount: total,
      fuelDiscount: savings,
      totalAmount: total,
      selected: true
    };
  };

  // Parse Murphy USA receipts
  const parseMurphy = (section: string): ParsedFuelExpense | null => {
    if (!section.match(/Murphy\s*USA/i)) return null;
    
    const date = parseDate(section);
    if (!date) return null;
    
    // Extract store number
    const storeMatch = section.match(/Murphy\s*USA\s*(\d+)/i);
    const cityMatch = section.match(/([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*),?\s+([A-Z]{2})\s+\d{5}/i);
    
    let location = 'Murphy USA';
    if (storeMatch) location += ` #${storeMatch[1]}`;
    if (cityMatch) location += ` - ${cityMatch[1]}, ${cityMatch[2]}`;
    
    // Extract gallons - QTY(GAL) or OTY(GAL) format
    const gallonsMatch = section.match(/(?:QTY|OTY)\s*\(GAL\)[:\s]*(\d+\.?\d*)/i) ||
                        section.match(/(\d+\.\d+)\s*GAL/i);
    if (!gallonsMatch) return null;
    const gallons = parseFloat(gallonsMatch[1]);
    if (gallons < 0.5 || gallons > 200) return null;
    
    // Extract total
    const totalMatch = section.match(/(?:FUEL TOTAL|NET TOTAL)[:\s]*\$?\s*([\d.]+)/i);
    if (!totalMatch) return null;
    const total = parseFloat(totalMatch[1]);
    if (total < 3 || total > 500) return null;
    
    return {
      id: crypto.randomUUID(),
      date,
      location,
      fuelAmount: total,
      fuelDiscount: 0,
      totalAmount: total,
      selected: true
    };
  };

  // Parse 7-Eleven receipts
  const parse7Eleven = (section: string): ParsedFuelExpense | null => {
    if (!section.match(/7-ELEVEN|7-Eleven/i)) return null;
    
    const date = parseDate(section);
    if (!date) return null;
    
    // Extract store number
    const storeMatch = section.match(/7-ELEVEN\s*(\d+)/i);
    const cityMatch = section.match(/([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)\s*,?\s+([A-Z]{2})\s+\d{5}/i) ||
                     section.match(/([A-Z\s]+),\s*([A-Z]{2})/);
    
    let location = '7-Eleven';
    if (storeMatch) location += ` #${storeMatch[1]}`;
    if (cityMatch) location += ` - ${cityMatch[1].trim()}, ${cityMatch[2]}`;
    
    // Extract gallons - "Regular X.XXXG" format
    const gallonsMatch = section.match(/(?:Regular|Unleaded|Premium)\s+(\d+\.?\d*)G/i) ||
                        section.match(/(\d+\.\d+)\s*GAL/i);
    if (!gallonsMatch) return null;
    const gallons = parseFloat(gallonsMatch[1]);
    if (gallons < 0.5 || gallons > 200) return null;
    
    // Extract total
    const totalMatch = section.match(/FUEL TOTAL[:\s]*\$?\s*([\d.]+)/i) ||
                      section.match(/TOTAL\s*=\s*\$?\s*([\d.]+)/i);
    if (!totalMatch) return null;
    const total = parseFloat(totalMatch[1]);
    if (total < 3 || total > 500) return null;
    
    return {
      id: crypto.randomUUID(),
      date,
      location,
      fuelAmount: total,
      fuelDiscount: 0,
      totalAmount: total,
      selected: true
    };
  };

  // Parse Marathon receipts
  const parseMarathon = (section: string): ParsedFuelExpense | null => {
    if (!section.match(/MARATHON/i)) return null;
    
    const date = parseDate(section);
    if (!date) return null;
    
    // Extract store number
    const storeMatch = section.match(/MARATHON\s*(\d+)/i);
    const cityMatch = section.match(/([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)\s*,?\s+([A-Z]{2})\s+\d{5}/i) ||
                     section.match(/([A-Z\s]+),\s*([A-Z]{2})/);
    
    let location = 'Marathon';
    if (storeMatch) location += ` #${storeMatch[1]}`;
    if (cityMatch) location += ` - ${cityMatch[1].trim()}, ${cityMatch[2]}`;
    
    // Extract gallons - "REGULAR XX.XXXG" format
    const gallonsMatch = section.match(/(?:REGULAR|UNLEADED|PREMIUM)\s+(\d+\.?\d*)G/i);
    if (!gallonsMatch) return null;
    const gallons = parseFloat(gallonsMatch[1]);
    if (gallons < 0.5 || gallons > 200) return null;
    
    // Extract total
    const totalMatch = section.match(/FUEL TOTAL[:\s]*\$?\s*([\d.]+)/i) ||
                      section.match(/TOTAL\s*=\s*\$?\s*([\d.]+)/i);
    if (!totalMatch) return null;
    const total = parseFloat(totalMatch[1]);
    if (total < 3 || total > 500) return null;
    
    return {
      id: crypto.randomUUID(),
      date,
      location,
      fuelAmount: total,
      fuelDiscount: 0,
      totalAmount: total,
      selected: true
    };
  };

  // Parse Speedway receipts
  const parseSpeedway = (section: string): ParsedFuelExpense | null => {
    if (!section.match(/SPEEDWAY/i)) return null;
    
    const date = parseDate(section);
    if (!date) return null;
    
    // Extract store number
    const storeMatch = section.match(/STORE\s*#?[:\s]*(\d+)/i);
    const cityMatch = section.match(/([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)\s+(\d{5})/i);
    
    let location = 'Speedway';
    if (storeMatch) location += ` #${storeMatch[1]}`;
    if (cityMatch) location += ` - ${cityMatch[1]}`;
    
    // Extract gallons - "X.XXX GAL @ $X.XXX /GAL" format
    const gallonsMatch = section.match(/(\d+\.?\d*)\s*GAL\s*@/i) ||
                        section.match(/Pump\s+\d+\s+\w+\s+(\d+\.?\d*)/i);
    if (!gallonsMatch) return null;
    const gallons = parseFloat(gallonsMatch[1]);
    if (gallons < 0.5 || gallons > 200) return null;
    
    // Extract total
    const totalMatch = section.match(/TOTAL\s*(?:DUE)?\s*\$?\s*([\d.]+)/i);
    if (!totalMatch) return null;
    const total = parseFloat(totalMatch[1]);
    if (total < 3 || total > 500) return null;
    
    return {
      id: crypto.randomUUID(),
      date,
      location,
      fuelAmount: total,
      fuelDiscount: 0,
      totalAmount: total,
      selected: true
    };
  };

  // Parse JJ's receipts
  const parseJJs = (section: string): ParsedFuelExpense | null => {
    if (!section.match(/JJ'S|JJ's/i)) return null;
    
    const date = parseDate(section);
    if (!date) return null;
    
    // Extract store number
    const storeMatch = section.match(/JJ'S\s*#?\s*(\d+)/i);
    
    let location = "JJ's";
    if (storeMatch) location += ` #${storeMatch[1]}`;
    
    // Extract gallons
    const gallonsMatch = section.match(/GALLONS[:\s]*(\d+\.?\d*)/i);
    if (!gallonsMatch) return null;
    const gallons = parseFloat(gallonsMatch[1]);
    if (gallons < 0.5 || gallons > 200) return null;
    
    // Extract total
    const totalMatch = section.match(/FUEL SALE[:\s]*\$?\s*([\d.]+)/i) ||
                      section.match(/CREDIT[:\s]*\$?\s*([\d.]+)/i);
    if (!totalMatch) return null;
    const total = parseFloat(totalMatch[1]);
    if (total < 3 || total > 500) return null;
    
    return {
      id: crypto.randomUUID(),
      date,
      location,
      fuelAmount: total,
      fuelDiscount: 0,
      totalAmount: total,
      selected: true
    };
  };

  // Parse individual fuel receipts (Love's, Pilot, Flying J, Murphy, etc.)
  const parseIndividualReceipts = (text: string): ParsedFuelExpense[] => {
    const fuelExpenses: ParsedFuelExpense[] = [];
    
    // Split by receipt boundaries - look for store names or receipt markers
    const sections = text.split(/(?=Love's|Welcome To Loves|Pilot\s*#|Flying J\s*#|Murphy USA|7-ELEVEN|SPEEDWAY|MARATHON|JJ'S)/i);
    
    for (const section of sections) {
      if (!section.trim() || section.length < 30) continue;
      
      // Try each fuel parser
      let fuel: ParsedFuelExpense | null = null;
      
      fuel = parseLoves(section);
      if (!fuel) fuel = parsePilotFlyingJ(section);
      if (!fuel) fuel = parseMurphy(section);
      if (!fuel) fuel = parse7Eleven(section);
      if (!fuel) fuel = parseMarathon(section);
      if (!fuel) fuel = parseSpeedway(section);
      if (!fuel) fuel = parseJJs(section);
      
      if (fuel) {
        // Check for duplicates
        const exists = fuelExpenses.some(f => 
          f.date === fuel!.date && 
          Math.abs(f.totalAmount - fuel!.totalAmount) < 0.01
        );
        if (!exists) fuelExpenses.push(fuel);
      }
    }
    
    return fuelExpenses;
  };

  // Parse Comdata statement text (centralized format)
  // Format: DATE | TIME | DESCRIPTION | ORDER # | FUEL AMOUNT | FUEL DISCL. | CASH ADVANCE | OTHER | CARD FEE | TOTAL
  const parseComdataStatement = (text: string): ParsedFuelExpense[] => {
    const fuelExpenses: ParsedFuelExpense[] = [];
    
    // Fuel station patterns - must include # and location for Comdata format
    const fuelStationPattern = /(PILOT|LOVES|LOVE'S|FLYING\s*J|SAPP|PETRO|SHEETZ|SHEETS|WAWA|7-ELEVEN|TA\b|AMBEST|CEFCO|CASEY|KWIK|QUIKTRIP|QT|RACETRAC|ROAD\s*RANGER|SHELL|EXXON|CHEVRON|BP|MARATHON|SPEEDWAY|MURPHY|CIRCLE\s*K)\s*#?\s*(\d+)?[\/\s]*([A-Z]+)?/i;
    
    // Split text into segments that might contain transactions
    // Look for date patterns followed by transaction data
    // Amounts: \d*\.\d{2}-? handles both "75.68-" (trailing minus) and ".00" (no leading digit)
    const transactionPattern = /(\d{1,2}\/\d{1,2}\/\d{2})\s+(\d{1,2}:\d{2})\s+([A-Z0-9#\/\s\-]+?)\s+(\d*\.\d{2})-?\s+(\d*\.\d{2})-?\s+(\d*\.\d{2})-?\s+(\d*\.\d{2})-?\s+(\d*\.\d{2})-?\s+(\d*\.\d{2})-?/gi;

    const parseAmt = (s: string) => parseFloat(s.replace(/^\./, '0.'));

    let match;
    while ((match = transactionPattern.exec(text)) !== null) {
      const [, date, time, description, fuelAmount, fuelDisc, cashAdv, other, cardFee, total] = match;

      const fuelAmt = parseAmt(fuelAmount);
      const totalAmt = parseAmt(total);
      const discount = parseAmt(fuelDisc);
      
      // Only process if FUEL AMOUNT > 0 (this filters out tolls and advances)
      if (fuelAmt > 0) {
        // Format date
        const dateParts = date.split('/');
        const formattedDate = `20${dateParts[2]}-${dateParts[0].padStart(2, '0')}-${dateParts[1].padStart(2, '0')}`;
        
        // Clean up description - extract station name and location
        const descClean = description.trim();
        
        // Dedup by date + total + station identity (prevents FLYING J vs TA EFFINGHAM false-dedup)
        const stationId = descClean.split('/')[0].trim().toUpperCase();
        const exists = fuelExpenses.some(f => {
          if (f.date !== formattedDate || Math.abs(f.totalAmount - totalAmt) >= 0.01) return false;
          const existingStation = f.location.split(' - ')[0].toUpperCase();
          return existingStation === stationId;
        });
        
        if (!exists) {
          // Format location nicely: "PILOT #43/WALCOTT" -> "PILOT #43 - Walcott"
          let formattedLocation = descClean;
          const locMatch = descClean.match(/^([A-Z0-9\s\-#]+)\/([A-Z\s]+)$/i);
          if (locMatch) {
            const station = locMatch[1].trim();
            const city = locMatch[2].trim().split(' ')
              .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
              .join(' ');
            formattedLocation = `${station} - ${city}`;
          }
          
          fuelExpenses.push({
            id: crypto.randomUUID(),
            date: formattedDate,
            location: formattedLocation,
            fuelAmount: fuelAmt,
            fuelDiscount: discount,
            totalAmount: totalAmt,
            selected: true
          });
        }
      }
    }
    
    // Fallback: Try simpler line-by-line parsing if regex didn't find anything
    if (fuelExpenses.length === 0) {
      const lines = text.split(/\n|(?=\d{2}\/\d{2}\/\d{2}\s+\d{1,2}:\d{2})/);
      
      for (const line of lines) {
        // Look for fuel station names with city: "PILOT #43/WALCOTT"
        const fullStationMatch = line.match(/(PILOT|LOVES|LOVE'S|FLYING\s*J|SHEETZ|SHEETS|7-ELEVEN|TA\b|SAPP|PETRO|AMBEST|JJ'S\s*TRAVEL|CEFCO|CASEY|KWIK|QUIKTRIP|QT|RACETRAC|ROAD\s*RANGER|SHELL|EXXON|CHEVRON|BP|MARATHON|SPEEDWAY|MURPHY|CIRCLE\s*K)\s*#?\s*(\d+)?[\/\s]*([A-Z]+)?/i);
        if (!fullStationMatch) continue;

        // Get date
        const dateMatch = line.match(/(\d{1,2}\/\d{1,2}\/\d{2})/);
        if (!dateMatch) continue;

        // Get all numbers — handles trailing minus (75.68-) and leading dot (.00)
        const numbers = line.match(/\d*\.\d{2}-?/g)
          ?.map(n => n.replace('-', '').replace(/^\./, '0.'));
        if (!numbers || numbers.length < 2) continue;

        // First non-zero number > $10 is fuel amount
        const fuelAmt = numbers.find(n => parseFloat(n) > 10 && parseFloat(n) < 500);
        if (!fuelAmt) continue;

        const fuelAmt1Idx = numbers.indexOf(fuelAmt);
        // Second non-zero number after fuel is discount
        const discountNum = fuelAmt1Idx + 1 < numbers.length ? parseFloat(numbers[fuelAmt1Idx + 1]) : 0;
        const total = parseFloat(numbers[numbers.length - 1]);
        const fuelAmtNum = parseFloat(fuelAmt);

        // Fuel amount should be less than or close to total (discount applied)
        if (fuelAmtNum > total * 1.5) continue;

        const dateParts = dateMatch[1].split('/');
        const formattedDate = `20${dateParts[2]}-${dateParts[0].padStart(2, '0')}-${dateParts[1].padStart(2, '0')}`;
        
        // Format location with city
        const stationName = fullStationMatch[1].trim();
        const stationNum = fullStationMatch[2] ? `#${fullStationMatch[2]}` : '';
        const city = fullStationMatch[3] ? 
          fullStationMatch[3].charAt(0).toUpperCase() + fullStationMatch[3].slice(1).toLowerCase() : '';
        const formattedLocation = city ? 
          `${stationName} ${stationNum} - ${city}`.trim() : 
          `${stationName} ${stationNum}`.trim();
        
        const existingStationKey = `${stationName} ${stationNum}`.trim().toUpperCase();
        const exists = fuelExpenses.some(f => {
          if (f.date !== formattedDate || Math.abs(f.totalAmount - total) >= 0.01) return false;
          const existingStation = f.location.split(' - ')[0].toUpperCase();
          return existingStation === existingStationKey;
        });
        
        if (!exists) {
          fuelExpenses.push({
            id: crypto.randomUUID(),
            date: formattedDate,
            location: formattedLocation,
            fuelAmount: fuelAmtNum,
            fuelDiscount: discountNum < fuelAmtNum ? discountNum : 0,
            totalAmount: total,
            selected: true
          });
        }
      }
    }
    
    return fuelExpenses;
  };


  // Parse Chevron/Texaco receipts
  // Format: Station #, CHEVRON branding, AUTO DSL or DIESEL, date at bottom
  const parseChevronReceipts = (text: string): ParsedFuelExpense[] => {
    const expenses: ParsedFuelExpense[] = [];
    console.log('=== CHEVRON PARSER ===');
    
    // Check if this looks like a Chevron receipt
    const isChevron = /CHEVRON|TEXACO|DONCO/i.test(text);
    if (!isChevron) {
      console.log('Not a Chevron receipt');
      return expenses;
    }
    
    // Split by potential receipt blocks (station numbers or CHEVRON headers)
    const receiptBlocks = text.split(/(?=(?:Stn#|CHEVRON|DONCO\s*#)\s*\d)/i);
    console.log('Found', receiptBlocks.length, 'Chevron receipt blocks');
    
    for (const block of receiptBlocks) {
      if (block.trim().length < 50) continue;
      
      // Check if this is a fuel receipt (has DIESEL, DSL, or gallons)
      const isFuel = /DSL|DIESEL|UNLEADED|REGULAR|PREMIUM|@ [\d.]+\/\s*G/i.test(block);
      if (!isFuel) {
        console.log('Skipping non-fuel Chevron receipt');
        continue;
      }
      
      // Extract station number
      const stationMatch = block.match(/(?:Stn#|Station)\s*(\d+)/i) ||
                          block.match(/DONCO\s*#(\d+)/i) ||
                          block.match(/(\d{8})/); // 8-digit station ID
      const stationNum = stationMatch ? stationMatch[1] : '';
      
      // Extract location (City, ST or just city/state from address)
      let city = '', state = '';
      const locationMatch = block.match(/([A-Z][A-Z\s]+)\s+([A-Z]{2})(?:\s|$)/m);
      if (locationMatch) {
        city = locationMatch[1].trim().replace(/\s+/g, ' ');
        state = locationMatch[2];
      }
      
      // Extract date - formats: M/DD/YY, MM/DD/YY, MM/DD/YYYY
      const dateMatch = block.match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
      if (!dateMatch) {
        console.log('Skipping Chevron block - no date found');
        continue;
      }
      let [, month, day, year] = dateMatch;
      // Fix 2-digit year
      if (year.length === 2) {
        year = parseInt(year) > 50 ? `19${year}` : `20${year}`;
      }
      const formattedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      
      // Extract invoice/transaction number
      const invMatch = block.match(/Inv#\s*(\d+)/i) ||
                      block.match(/TRAN#\s*(\d+)/i);
      const transactionId = invMatch ? invMatch[1] : '';
      
      // Extract card last 4
      const cardMatch = block.match(/\*{3,}(\d{4})/);
      const cardLast4 = cardMatch ? cardMatch[1] : '';
      
      // Extract gallons
      const gallonsMatch = block.match(/([\d.]+)\s*(?:GAL|G\s|@)/i) ||
                          block.match(/DSL[^@]*@?\s*([\d.]+)/i);
      const gallons = gallonsMatch ? parseFloat(gallonsMatch[1]) : 0;
      
      // Extract total amount
      let amount = 0;
      const totalMatch = block.match(/\*?\*?TOTAL\*?\*?[:\s]*\$?\s*([\d,]+\.?\d{0,2})/i) ||
                        block.match(/DEBIT\s*\$[:\s]*([\d,]+\.?\d{2})/i);
      if (totalMatch) {
        amount = parseFloat(totalMatch[1].replace(/,/g, ''));
      }
      
      // Skip invalid amounts
      if (isNaN(amount) || amount <= 0 || amount < 5 || amount > 500) {
        console.log(`Skipping Chevron block - invalid amount: $${amount}`);
        continue;
      }
      
      // Check for duplicates
      const isDupe = expenses.some(e => 
        e.date === formattedDate && Math.abs(e.totalAmount - amount) < 0.50
      );
      if (isDupe) continue;
      
      // Build location string
      let location = 'Chevron';
      if (stationNum) location += ` #${stationNum}`;
      if (city && state) location += ` - ${city}, ${state}`;
      
      console.log(`✓ Chevron fuel: ${location}, ${formattedDate}, $${amount}${gallons ? `, ${gallons} gal` : ''}`);
      
      expenses.push({
        id: crypto.randomUUID(),
        date: formattedDate,
        location,
        fuelAmount: amount,
        fuelDiscount: 0,
        totalAmount: amount,
        selected: true,
        storeNumber: stationNum,
        transactionId: transactionId,
        cardLast4: cardLast4,
      });
    }
    
    console.log('Chevron parser found:', expenses.length, 'fuel purchases');
    return expenses;
  };

  // Parse Pilot/Flying J receipts
  // Format: STORE XXX, date MM/DD/YYYY, Transaction #, DIESEL or Cash Adv Fee
  const parsePilotReceipts = (text: string): ParsedFuelExpense[] => {
    const expenses: ParsedFuelExpense[] = [];
    console.log('=== PILOT/FLYING J PARSER ===');
    
    // Check if this looks like a Pilot receipt (has Pilot/Flying J branding or pattern)
    const isPilot = /Pilot|Flying\s*J|Dilat/i.test(text); // "Dilat" is OCR error for "Pilot"
    if (!isPilot) {
      console.log('Not a Pilot receipt');
      return expenses;
    }
    
    // Split by store/receipt blocks - each receipt starts with STORE
    const receiptBlocks = text.split(/(?=STORE\s+\d{2,4})/i);
    console.log('Found', receiptBlocks.length, 'Pilot receipt blocks');
    
    for (const block of receiptBlocks) {
      if (block.trim().length < 50) continue;
      
      // Extract store number
      const storeMatch = block.match(/STORE\s+(\d{2,4})/i);
      const storeNum = storeMatch ? storeMatch[1] : '';
      
      // Extract location (City, ST pattern)
      let city = '', state = '';
      const locationMatch = block.match(/([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*),?\s*([A-Z]{2})\s+\d{5}/);
      if (locationMatch) {
        city = locationMatch[1].trim();
        state = locationMatch[2];
      }
      
      // Extract date - MM/DD/YYYY format
      const dateMatch = block.match(/(\d{2})\/(\d{2})\/(\d{4})/);
      if (!dateMatch) {
        console.log('Skipping Pilot block - no date found');
        continue;
      }
      const [, month, day, year] = dateMatch;
      const formattedDate = `${year}-${month}-${day}`;
      
      // Extract Transaction #
      const transMatch = block.match(/Transaction\s*#[:\s]*(\d+)/i);
      const transactionId = transMatch ? transMatch[1] : '';
      
      // Check what TYPE of transaction this is
      const isCashAdvance = /Cash\s*Adv|Comcheck|EFS\s*LLC|Cash\s*Due/i.test(block);
      const isFuelPurchase = /DIESEL|FUEL|DEF|Gallons/i.test(block);
      
      // Skip cash advances for now (they're fees, not deductible fuel)
      if (isCashAdvance && !isFuelPurchase) {
        console.log(`Skipping Pilot cash advance at Store ${storeNum} on ${formattedDate}`);
        continue;
      }
      
      // Extract total amount
      let amount = 0;
      
      // Look for Total line
      const totalMatch = block.match(/Total[:\s]*\$?\s*([\d,]+\.?\d{0,2})/i);
      if (totalMatch) {
        amount = parseFloat(totalMatch[1].replace(/,/g, ''));
      }
      
      // If this has DIESEL, try to get the diesel amount specifically
      if (isFuelPurchase) {
        const dieselMatch = block.match(/DIESEL[:\s]*\$?\s*([\d,]+\.?\d{2})/i);
        if (dieselMatch) {
          amount = parseFloat(dieselMatch[1].replace(/,/g, ''));
        }
      }
      
      // Extract gallons if available
      const gallonsMatch = block.match(/Gallons[:\s]*([\d.]+)/i);
      const gallons = gallonsMatch ? parseFloat(gallonsMatch[1]) : 0;
      
      // Skip invalid amounts
      if (isNaN(amount) || amount <= 0 || amount < 5 || amount > 500) {
        console.log(`Skipping Pilot block - invalid amount: $${amount}`);
        continue;
      }
      
      // Check for duplicates within this parse
      const isDupe = expenses.some(e => 
        e.date === formattedDate && Math.abs(e.totalAmount - amount) < 0.50
      );
      if (isDupe) {
        console.log(`Skipping duplicate: Store ${storeNum}, ${formattedDate}, $${amount}`);
        continue;
      }
      
      // Build location string
      let location = 'Pilot';
      if (storeNum) location += ` #${storeNum}`;
      if (city && state) location += ` - ${city}, ${state}`;
      
      console.log(`✓ Pilot fuel: Store ${storeNum}, ${formattedDate}, $${amount}${gallons ? `, ${gallons} gal` : ''}`);
      
      expenses.push({
        id: crypto.randomUUID(),
        date: formattedDate,
        location,
        fuelAmount: amount,
        fuelDiscount: 0,
        totalAmount: amount,
        selected: true,
        storeNumber: storeNum,
        transactionId: transactionId,
      });
    }
    
    console.log('Pilot parser found:', expenses.length, 'fuel purchases');
    return expenses;
  };

  // Master parse function - tries both statement and individual receipts
  const parseComdataText = (text: string): ParsedFuelExpense[] => {
    let allFuel: ParsedFuelExpense[] = [];
    
    console.log('=== PARSING FUEL TEXT ===');
    console.log('Raw text length:', text.length);
    
    // Check if this is a Chevron/Texaco receipt first
    const isChevron = /CHEVRON|TEXACO|DONCO/i.test(text);
    if (isChevron) {
      console.log('Detected Chevron/Texaco receipt');
      const chevronFuel = parseChevronReceipts(text);
      if (chevronFuel.length > 0) {
        return chevronFuel; // Return early - Chevron parser handles this format
      }
    }
    
    // Check if this is a Pilot/Flying J receipt
    const isPilot = /Pilot|Flying\s*J|Dilat/i.test(text);
    if (isPilot) {
      console.log('Detected Pilot/Flying J receipt');
      const pilotFuel = parsePilotReceipts(text);
      if (pilotFuel.length > 0) {
        return pilotFuel; // Return early - Pilot parser handles this format
      }
    }
    
    // Check if this looks like clean OCR text (receipts start with ~ •aves or ~ •oves)
    const isCleanOCR = /~\s*•?[ao]ves\.?\s*STORE/i.test(text);
    console.log('Is clean OCR format:', isCleanOCR);
    
    // Try clean OCR parser first if detected
    if (isCleanOCR) {
      console.log('Using OCR parser for clean Love\'s receipts');
      const ocrFuel = parseLovesOCR(text);
      console.log('OCR parser found:', ocrFuel.length);
      if (ocrFuel.length > 0) {
        return ocrFuel; // Return early - OCR parser handles this format best
      }
    }
    
    // Normalize text ONCE for all parsers
    const normalized = normalizeText(text);
    console.log('Normalized text length:', normalized.length);
    console.log('Normalized sample:', normalized.substring(0, 300));
    
    // Check if this looks like a Love's binder (fleet card receipts)
    const isLovesBinder = /TRKDS|DFCQ|Love's.*STORE|STORE.*Love's/i.test(normalized);
    console.log('Is Love\'s binder:', isLovesBinder);
    
    // Try Love's Fleet Card format first (TRKDS/DFCQ from binder PDFs)
    const fleetFuel = parseLovesFleetCard(normalized);
    console.log('Fleet card parser found:', fleetFuel.length);
    allFuel = [...fleetFuel];
    
    // Only try statement format if this doesn't look like a Love's binder
    // (statement parser produces garbage from Love's receipt PDFs)
    if (!isLovesBinder || fleetFuel.length === 0) {
      const statementFuel = parseComdataStatement(normalized);
      console.log('Statement parser found:', statementFuel.length);
      allFuel = [...allFuel, ...statementFuel];
    }
    
    // Also try individual receipts format (they might be combined)
    const receiptFuel = parseIndividualReceipts(normalized);
    console.log('Receipt parser found:', receiptFuel.length);
    allFuel = [...allFuel, ...receiptFuel];
    
    // Remove duplicates based on date + amount (within $1)
    const uniqueFuel = allFuel.filter((fuel, index, self) =>
      index === self.findIndex(f => 
        f.date === fuel.date && Math.abs(f.totalAmount - fuel.totalAmount) < 1.00
      )
    );
    
    console.log('Total unique fuel entries:', uniqueFuel.length);
    return uniqueFuel;
  };

  // Extract text from PDF
  const extractTextFromPDF = async (file: File): Promise<string> => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    
    let fullText = '';
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items
        .map((item: any) => item.str)
        .join(' ');
      fullText += pageText + '\n\n';
    }
    
    return fullText;
  };

  // Handle file drop
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;
    
    setIsLoading(true);
    setError(null);
    setImportStatus(null);
    
    try {
      const text = await extractTextFromPDF(file);
      setPdfText(text);
      
      const fuelExpenses = parseComdataText(text);
      
      if (fuelExpenses.length === 0) {
        setError('No fuel expenses found. Try pasting the text manually below.');
      } else {
        setParsedFuel(fuelExpenses);
      }
    } catch (e: any) {
      console.error('PDF extraction error:', e);
      setError(`Failed to read PDF: ${e.message}. Try pasting the text manually.`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: 1
  });

  // Parse from pasted text
  const handleParseText = () => {
    if (!pdfText.trim()) return;
    
    setError(null);
    const fuelExpenses = parseComdataText(pdfText);
    
    if (fuelExpenses.length === 0) {
      setError('No fuel expenses found in the text.');
    } else {
      setParsedFuel(fuelExpenses);
    }
  };

  // Toggle selection
  const toggleFuelSelection = (id: string) => {
    setParsedFuel(prev => prev.map(f => f.id === id ? { ...f, selected: !f.selected } : f));
  };

  // Remove items
  const removeFuel = (id: string) => {
    setParsedFuel(prev => prev.filter(f => f.id !== id));
  };

  // Import selected items
  const handleImport = async () => {
    const selectedFuel = parsedFuel.filter(f => f.selected);
    
    if (selectedFuel.length === 0) return;
    
    setIsLoading(true);
    let fuelCount = 0;
    let errors = 0;
    let skipped = 0;
    
    // Import fuel expenses
    for (const fuel of selectedFuel) {
      // Check for duplicates before importing
      if (isDuplicate(fuel)) {
        console.warn(`Skipping duplicate: ${fuel.location} on ${fuel.date}`);
        skipped++;
        continue;
      }
      
      try {
        // Determine the date to use - override year if targetYear is set
        let dateToUse = fuel.date;
        if (targetYear !== 'auto' && fuel.date) {
          const dateParts = fuel.date.split('-');
          if (dateParts.length === 3) {
            dateToUse = `${targetYear}-${dateParts[1]}-${dateParts[2]}`;
          }
        }
        
        await addExpense({
          category: 'fuel',
          description: `${fuel.location} - Comdata`,
          amount: fuel.fuelAmount,
          date: dateToUse,
          file: new Blob([`Fuel purchase at ${fuel.location}`], { type: 'text/plain' }),
          filename: `fuel-${fuel.location.toLowerCase().replace(/[^a-z0-9]/g, '-')}-${dateToUse}.pdf`,
          // Source tracking for smart duplicate detection
          source: 'loves_receipt',
          storeNumber: fuel.storeNumber,
          cardLast4: fuel.cardLast4,
          transactionId: fuel.transactionId,
        });
        fuelCount++;
      } catch (e) {
        console.error('Failed to import fuel:', e);
        errors++;
      }
    }
    
    setImportStatus({ fuel: fuelCount, errors, skipped });
    
    // Remove imported AND skipped items from list
    if (fuelCount > 0 || skipped > 0) {
      setParsedFuel(prev => prev.filter(f => !f.selected));
    }
    
    setIsLoading(false);
  };

  // Calculate counts excluding duplicates
  const duplicateCount = useMemo(() => 
    parsedFuel.filter(f => isDuplicate(f)).length
  , [parsedFuel, isDuplicate]);
  
  const selectedFuelCount = parsedFuel.filter(f => f.selected && !isDuplicate(f)).length;
  const totalFuelAmount = parsedFuel
    .filter(f => f.selected && !isDuplicate(f))
    .reduce((sum, f) => sum + f.fuelAmount, 0);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white flex items-center gap-3">
          <Fuel className="w-8 h-8 text-orange-400" />
          Import Comdata Statement
        </h1>
        <p className="text-shuttle-sky mt-1">
          Upload your fuel receipts or Comdata statement to import fuel expenses
        </p>
      </div>

      {/* Target Tax Year Selector */}
      <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 backdrop-blur-md rounded-2xl p-4 border border-amber-500/30">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="font-semibold text-white flex items-center gap-2">
              📅 Target Tax Year
            </h3>
            <p className="text-xs text-white/60">
              {targetYear === 'auto' 
                ? 'Year will be detected from receipt dates' 
                : `All imports will be saved to ${targetYear}`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={targetYear}
              onChange={(e) => setTargetYear(e.target.value === 'auto' ? 'auto' : parseInt(e.target.value))}
              className="px-4 py-2 bg-shuttle-dark border-2 border-amber-500/50 rounded-xl text-white font-medium focus:ring-2 focus:ring-amber-500 transition-all"
            >
              <option value="auto">🔄 Auto-detect from date</option>
              {availableYears.map(year => (
                <option key={year} value={year}>
                  📋 {year} {year < currentYear ? '(Back taxes)' : ''}
                </option>
              ))}
            </select>
          </div>
        </div>
        {targetYear !== 'auto' && targetYear < currentYear && (
          <div className="mt-3 p-2 bg-orange-500/20 rounded-lg border border-orange-500/30">
            <p className="text-sm text-orange-200">
              ⚠️ Importing to <strong>{targetYear}</strong> - make sure these fuel receipts are from that year!
            </p>
          </div>
        )}
      </div>

      {/* Bulk Upload - uploads to storage, processes as fuel */}
      <BulkUploadSection category="fuel" targetYear={targetYear} onError={setError} />

      {/* Upload Zone */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all ${
          isDragActive 
            ? 'border-orange-400 bg-orange-400/10' 
            : 'border-white/30 hover:border-white/50 bg-white/5'
        }`}
      >
        <input {...getInputProps()} />
        <FileUp className="w-12 h-12 mx-auto mb-4 text-orange-400" />
        {isDragActive ? (
          <p className="text-white text-lg">Drop the Comdata PDF here...</p>
        ) : (
          <>
            <p className="text-white text-lg mb-2">
              Drag & drop your Comdata statement PDF here
            </p>
            <p className="text-white/60 text-sm">or click to browse</p>
          </>
        )}
      </div>

      {/* Manual Text Input */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
        <h3 className="text-lg font-medium text-white mb-3 flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Or Paste Statement Text
        </h3>
        <textarea
          value={pdfText}
          onChange={(e) => setPdfText(e.target.value)}
          placeholder="Paste the text content from your Comdata statement here..."
          className="w-full h-32 bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-orange-400 resize-none font-mono text-sm"
        />
        <div className="mt-3 flex gap-3">
          <button
            onClick={handleParseText}
            disabled={!pdfText.trim()}
            className="px-6 py-2 bg-orange-500/20 text-orange-400 rounded-lg hover:bg-orange-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Parse Statement
          </button>
          <button
            onClick={() => {
              setPdfText('');
              setParsedFuel([]);
              setError(null);
              setImportStatus(null);
            }}
            disabled={!pdfText.trim() && parsedFuel.length === 0}
            className="px-6 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg hover:bg-cyan-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
          <span className="ml-3 text-white">Processing...</span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-red-200">{error}</p>
        </div>
      )}

      {/* Import Status */}
      {importStatus && (
        <div className={`${importStatus.fuel > 0 ? 'bg-green-500/20 border-green-500/50' : 'bg-yellow-500/20 border-yellow-500/50'} border rounded-xl p-4 flex items-start gap-3`}>
          <CheckCircle className={`w-5 h-5 ${importStatus.fuel > 0 ? 'text-green-400' : 'text-yellow-400'} flex-shrink-0 mt-0.5`} />
          <div>
            <p className={importStatus.fuel > 0 ? 'text-green-200' : 'text-yellow-200'}>
              {importStatus.fuel > 0 && `Imported ${importStatus.fuel} fuel expense(s)!`}
              {importStatus.errors > 0 && ` ${importStatus.errors} failed.`}
            </p>
            {importStatus.skipped > 0 && (
              <p className="text-yellow-300 text-sm mt-1">
                ⚠️ {importStatus.skipped} duplicate(s) skipped - already in database!
              </p>
            )}
          </div>
        </div>
      )}

      {/* Parsed Fuel Expenses */}
      {parsedFuel.length > 0 && (
        <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 overflow-hidden">
          <div className="p-4 border-b border-white/10 flex items-center justify-between bg-orange-500/10 flex-wrap gap-2">
            <div>
              <h3 className="text-lg font-medium text-white flex items-center gap-2">
                ⛽ Fuel Expenses ({parsedFuel.length})
              </h3>
              {duplicateCount > 0 && (
                <p className="text-yellow-400 text-sm">
                  ⚠️ {duplicateCount} already in database
                </p>
              )}
            </div>
            <span className="text-orange-400 font-medium">
              {selectedFuelCount} new • ${totalFuelAmount.toFixed(2)}
            </span>
          </div>

          <div className="divide-y divide-white/10 max-h-80 overflow-y-auto">
            {parsedFuel.map((fuel) => {
              const isExisting = isDuplicate(fuel);
              return (
                <div
                  key={fuel.id}
                  className={`p-4 flex items-center gap-4 transition-colors ${
                    isExisting ? 'bg-yellow-500/10 opacity-60' : fuel.selected ? 'bg-white/5' : 'bg-transparent opacity-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={fuel.selected && !isExisting}
                    onChange={() => !isExisting && toggleFuelSelection(fuel.id)}
                    disabled={isExisting}
                    className={`w-5 h-5 rounded border-white/30 bg-white/10 text-orange-400 focus:ring-orange-400 ${isExisting ? 'cursor-not-allowed opacity-50' : ''}`}
                  />
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {isExisting && (
                        <span className="px-2 py-0.5 rounded text-xs font-medium border bg-yellow-500/20 text-yellow-400 border-yellow-500/50">
                          DUPLICATE
                        </span>
                      )}
                      <p className="font-medium text-white truncate">{fuel.location}</p>
                    </div>
                    <p className="text-sm text-white/60">{fuel.date}</p>
                  </div>

                  <div className="text-right">
                    <p className="text-lg font-bold text-orange-400">${fuel.fuelAmount.toFixed(2)}</p>
                    <p className="text-xs text-white/50">
                      {fuel.fuelDiscount > 0 && <span className="text-green-400">-${fuel.fuelDiscount.toFixed(2)} disc</span>}
                      {fuel.fuelDiscount > 0 && ' • '}
                      Total: ${fuel.totalAmount.toFixed(2)}
                    </p>
                  </div>

                  <button
                    onClick={() => removeFuel(fuel.id)}
                    className="p-2 text-white/40 hover:text-red-400 transition-colors"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Import Button */}
      {parsedFuel.length > 0 && (
        <div className="flex justify-end">
          <button
            onClick={handleImport}
            disabled={selectedFuelCount === 0 || isLoading}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-medium hover:from-orange-400 hover:to-amber-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg"
          >
            <Save className="w-5 h-5" />
            Import {selectedFuelCount} Fuel Expense(s)
          </button>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
        <h3 className="text-lg font-medium text-white mb-3">📋 How to Import Fuel Expenses</h3>
        <ol className="space-y-2 text-white/70 text-sm">
          <li>1. Download your Comdata statement OR collect fuel receipts</li>
          <li>2. Drag & drop the PDF here (or paste the text)</li>
          <li>3. Review the parsed fuel purchases</li>
          <li>4. Click "Import" to save to cloud</li>
        </ol>
        <div className="mt-4 p-3 bg-orange-500/10 rounded-lg border border-orange-500/30">
          <p className="text-orange-300 text-sm">
            💡 <strong>Pro tip:</strong> Fuel expenses are 100% deductible for business use!
          </p>
        </div>
      </div>
    </div>
  );
}

